/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { countryCodes, stateCodes } from '../constants';
import { supportUSAndCanadaAddressInputs, locale } from '../config';
import { diagnostics } from '@utils/diagnostics';
import { get, concat } from '@utils/lodash';

declare const global: any;

const windowWrapper = window;
export const getGooglePlaces = (): any => get(windowWrapper, 'google.maps.places');
const getGoogleEvent = (): any => get(windowWrapper, 'google.maps.event');

export const getGooglePlacesCountryRestriction = function (): string | string[] {
  // Make sure that this is always less than 5. Google Places API currently only supports 5 country codes
  const usPlaces = concat([countryCodes.USA], stateCodes.USTerritories);
  // this config should not respect the locale. There are some banks that support both address inputs regardless of locale
  if (supportUSAndCanadaAddressInputs) {
    // Google Places API currently only supports 5 country codes so only including US territories with highest populations
    return [countryCodes.CANADA, countryCodes.USA, stateCodes.GUAM, stateCodes.PUERTO_RICO, stateCodes.VIRGIN_ISLANDS];
  }

  switch (locale) {
    case 'en_US':
      return usPlaces;
    case 'en_CA':
      return countryCodes.CANADA;
    default:
      return usPlaces;
  }
};

export function isPlacesLoaded() {
  if (!getGooglePlaces() && !get(global, 'IS_TEST')) {
    diagnostics.warn('Google places autocomplete service not loaded, likely because of an aggressive adblocker.');
  }
  return !!getGooglePlaces();
}

export function isEventLoaded() {
  if (!getGoogleEvent() && !get(global, 'IS_TEST')) {
    diagnostics.warn('Google places event handler not loaded, likely because of an aggressive adblocker.');
  }
  return !!getGoogleEvent();
}

export const autocompleteService: () => google.maps.places.AutocompleteService = () => {
  if (isPlacesLoaded()) {
    const googlePlaces = getGooglePlaces();
    return new googlePlaces.AutocompleteService();
  }

  return null;
};

export const generateNewSessionToken: () => google.maps.places.AutocompleteSessionToken = () => {
  if (isPlacesLoaded()) {
    const googlePlaces = getGooglePlaces();
    return new googlePlaces.AutocompleteSessionToken();
  }

  return null;
};

export type AutocompleteOrNull = {
  autocomplete: google.maps.places.Autocomplete;
  unsubscribe: () => void;
} | null;

export const autocompleteOnFieldServiceHelper = function (
  inputField: HTMLInputElement,
  listenerFunction?: () => void,
): AutocompleteOrNull {
  if (isPlacesLoaded() && isEventLoaded()) {
    const googlePlaces = getGooglePlaces();
    const country = getGooglePlacesCountryRestriction();
    const autocomplete = new googlePlaces.Autocomplete(inputField, {
      componentRestrictions: { country },
    });
    if (listenerFunction) {
      getGoogleEvent().addListener(autocomplete, 'place_changed', listenerFunction);
    }
    return {
      autocomplete,
      unsubscribe: () => getGoogleEvent().trigger(inputField.autocomplete, 'remove'),
    };
  }
  return null;
};

// some pages can load before google.maps.places service has attached, poll a few times to give service a chance
export const autocompleteOnFieldService = async function (
  inputField: HTMLInputElement,
  listenerFunction?: () => void,
): Promise<AutocompleteOrNull> {
  return new Promise<AutocompleteOrNull>(resolve => {
    const autocompleteOrNull = autocompleteOnFieldServiceHelper(inputField, listenerFunction);
    if (autocompleteOrNull) {
      resolve(autocompleteOrNull);
      return;
    }
    let retries = 0;
    const intervalId = setInterval(() => {
      if (retries > 100) {
        clearInterval(intervalId);
        resolve(null);
      }
      retries++;
      const autocompleteOrNullRetry = autocompleteOnFieldServiceHelper(inputField, listenerFunction);
      if (autocompleteOrNullRetry) {
        clearInterval(intervalId);
        resolve(autocompleteOrNullRetry);
      }
    }, 20);
  });
};

// Google maps requires passing in a DOM node to bind to, we create an empty div to serve this purpose.
const emptyDOMNode = windowWrapper.document.createElement('div');
export const detailsService: () => google.maps.places.PlacesService = () => {
  if (isPlacesLoaded()) {
    const googlePlaces = getGooglePlaces();
    return new googlePlaces.PlacesService(emptyDOMNode);
  }

  return null;
};

// Google exposes a PlacesServiceStatus object that lets us look up their status codes.
export const PlacesServiceStatus = () =>
  get(getGooglePlaces(), 'PlacesServiceStatus') as {
    [prop: string]: google.maps.places.PlacesServiceStatus;
  };
